import { useState, useEffect, useRef } from "react";
import { getReports, type ReportsData } from "../../api/reports.api";
import { useAuth } from '../../context/AuthContext';
import PremiumLock from '../../components/common/PremiumLock';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { exportReporteExcel } from '../../utils/excelGenerator';

export default function Reportes() {
  const { user } = useAuth();
  
  // Bloqueo Premium para modo Demo
  if (user?.company?.status === 'PRUEBA') {
    return <PremiumLock feature="reportes" />;
  }

  const [tipoReporte, setTipoReporte] = useState<string>("ingresos");
  const [periodo, setPeriodo] = useState<string>("hoy");
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportsData | null>(null);
  
  const reportRef = useRef<HTMLDivElement>(null);

  const colors = {
      teal: '#14B8A6',
      blue: '#3B82F6',
      violet: '#8B5CF6',
      emerald: '#10B981',
      amber: '#F59E0B',
      rose: '#F43F5E',
      gray: '#6B7280'
  };

  useEffect(() => {
     handlePeriodChange(periodo);
  }, []); // Initial load

  useEffect(() => {
     if (periodo !== 'personalizado') {
         fetchData();
     }
  }, [periodo, startDate, endDate]);

  const handlePeriodChange = (val: string) => {
      setPeriodo(val);
      const end = new Date();
      const start = new Date();

      if (val === 'hoy') {
          // Keep as today
      } else if (val === 'ayer') {
          start.setDate(start.getDate() - 1);
          end.setDate(end.getDate() - 1);
      } else if (val === 'semana') {
          const day = start.getDay() || 7; 
          if(day !== 1) start.setHours(-24 * (day - 1));
      } else if (val === 'mes') {
          start.setDate(1);
      }
      
      if (val !== 'personalizado') {
          setStartDate(start.toISOString().split('T')[0]);
          setEndDate(end.toISOString().split('T')[0]);
      }
  };

  const fetchData = async () => {
      setLoading(true);
      try {
          const result = await getReports(startDate, endDate, tipoReporte);
          setData(result);
      } catch (error) {
          console.error("Error fetching reports:", error);
      } finally {
          setLoading(false);
      }
  };

  const handleExportPDF = async () => {
      if (!reportRef.current) return;
      
      const canvas = await html2canvas(reportRef.current);
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`reporte-${tipoReporte}-${startDate}.pdf`);
  };

  const handleExportExcel = () => {
      if (!data) return;
      
      exportReporteExcel({
        data,
        tipoReporte,
        startDate,
        endDate,
        responsable: user?.nombre || 'Admin',
        puntoVenta: user?.company?.nombre || 'General'
      });
  };

  const reportesDisponibles = [
    { id: "ingresos", nombre: "Ingresos y Recaudación", icono: "ri-money-dollar-circle-line", color: "teal" },
    { id: "vehiculos", nombre: "Vehículos y Movimientos", icono: "ri-car-line", color: "blue" },
    { id: "ocupacion", nombre: "Ocupación de Espacios", icono: "ri-parking-box-line", color: "violet" },
    { id: "operadores", nombre: "Desempeño de Operadores", icono: "ri-user-line", color: "emerald" },
    { id: "auditoria", nombre: "Auditoría y Logs", icono: "ri-shield-check-line", color: "rose" },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getColorClasses = (color: string) => {
    const colorsMap: Record<string, string> = {
      teal: "bg-teal-50 text-teal-600 border-teal-200",
      blue: "bg-blue-50 text-blue-600 border-blue-200",
      violet: "bg-violet-50 text-violet-600 border-violet-200",
      emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
      amber: "bg-amber-50 text-amber-600 border-amber-200",
      rose: "bg-rose-50 text-rose-600 border-rose-200",
    };
    return colorsMap[color] || colorsMap.teal;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 h-64">
          <div className="flex flex-col items-center gap-4">
              <i className="ri-loader-4-line text-4xl animate-spin text-teal-600"></i>
              <p className="text-slate-500">Cargando datos...</p>
          </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center p-8 h-64">
          <div className="flex flex-col items-center gap-4">
              <i className="ri-error-warning-line text-4xl text-slate-400"></i>
              <p className="text-slate-500">No se pudieron cargar los datos del reporte.</p>
              <button 
                  onClick={fetchData}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition"
              >
                  Reintentar
              </button>
          </div>
      </div>
    );
  }

  return (
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Reportes y Análisis</h1>
            <p className="text-sm text-gray-500 mt-1">Genera reportes detallados del parqueadero</p>
          </div>
          <div className="flex gap-2">
            <button 
                onClick={handleExportExcel}
                disabled={!data}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
            >
                <i className="ri-file-excel-line"></i>
                Excel
            </button>
            <button 
                onClick={handleExportPDF}
                disabled={!data}
                className="px-4 py-2 bg-rose-600 text-white text-sm font-medium rounded-lg hover:bg-rose-700 transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
            >
                <i className="ri-file-pdf-line"></i>
                PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {reportesDisponibles.map((reporte) => (
            <button
              key={reporte.id}
              onClick={() => setTipoReporte(reporte.id)}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                tipoReporte === reporte.id
                  ? `${getColorClasses(reporte.color)} border-current`
                  : "bg-white border-gray-100 hover:border-gray-200"
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    tipoReporte === reporte.id ? "bg-white/50" : getColorClasses(reporte.color)
                  }`}
                >
                  <i className={`${reporte.icono} text-xl`}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{reporte.nombre}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
              <select
                value={periodo}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="hoy">Hoy</option>
                <option value="ayer">Ayer</option>
                <option value="semana">Esta Semana</option>
                <option value="mes">Este Mes</option>
                <option value="personalizado">Personalizado</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                    setStartDate(e.target.value);
                    setPeriodo('personalizado');
                }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                    setEndDate(e.target.value);
                    setPeriodo('personalizado');
                }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div className="flex items-end">
              <button 
                onClick={fetchData}
                disabled={loading}
                className="px-6 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {loading ? 'Generando...' : 'Generar'}
              </button>
            </div>
          </div>
        </div>

        {/* CONTENEDOR DE REPORTE (REFERENCIA PARA PDF) */}
        <div ref={reportRef} className="bg-slate-50 p-4 -m-4 rounded-xl"> 

        {data && tipoReporte === "ingresos" && (
          <div className="space-y-6 m-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Ingresos</p>
                    <p className="text-xl font-semibold text-gray-900">{formatCurrency(data.ingresos.total)}</p>
                  </div>
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-xl text-teal-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Efectivo</p>
                    <p className="text-xl font-semibold text-emerald-600">{formatCurrency(data.ingresos.efectivo)}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <i className="ri-cash-line text-xl text-emerald-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Tarjeta</p>
                    <p className="text-xl font-semibold text-blue-600">{formatCurrency(data.ingresos.tarjeta)}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                    <i className="ri-bank-card-line text-xl text-blue-600"></i>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                     <p className="text-sm text-gray-500 mb-1">Transferencia</p>
                     <p className="text-xl font-semibold text-violet-600">{formatCurrency(data.ingresos.qr)}</p>
                  </div>
                  <div className="w-12 h-12 bg-violet-50 rounded-lg flex items-center justify-center">
                     <i className="ri-bank-line text-xl text-violet-600"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Desglose de Ingresos</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                        { name: 'Efectivo', valor: data.ingresos.efectivo, fill: colors.emerald },
                        { name: 'Tarjeta', valor: data.ingresos.tarjeta, fill: colors.blue },
                        { name: 'Transferencia', valor: data.ingresos.qr, fill: colors.violet }
                    ]}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(val) => `$${val/1000}k`} />
                        <Tooltip formatter={(val: number) => formatCurrency(val)} />
                        <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                            {
                                [colors.emerald, colors.blue, colors.violet].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry} />
                                ))
                            }
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {data && tipoReporte === "vehiculos" && (
          <div className="space-y-6 m-4">
             <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Total</p>
                    <p className="text-2xl font-semibold text-gray-900">{data.vehiculos.total}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Carros</p>
                    <p className="text-2xl font-semibold text-blue-600">{data.vehiculos.carros}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Motos</p>
                    <p className="text-2xl font-semibold text-violet-600">{data.vehiculos.motos}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Promedio (Horas)</p>
                    <p className="text-2xl font-semibold text-gray-900">{data.vehiculos.promedio}h</p>
                </div>
             </div>
             
             <div className="bg-white rounded-xl border border-gray-100 p-6 flex justify-center">
                 <div className="h-64 w-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={[
                                    { name: 'Carros', value: data.vehiculos.carros },
                                    { name: 'Motos', value: data.vehiculos.motos }
                                ]}
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                            >
                                <Cell key="cell-0" fill={colors.blue} />
                                <Cell key="cell-1" fill={colors.violet} />
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                 </div>
             </div>
          </div>
        )}

        {data && tipoReporte === "ocupacion" && (
            <div className="space-y-6 m-4">
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                    <h3 className="text-lg font-bold mb-4">Ocupación Actual Calculada</h3>
                    <div className="flex items-center justify-center p-8">
                        <div className="text-center">
                            <span className="text-5xl font-bold text-violet-600">{data.ocupacion.actual}%</span>
                            <p className="text-slate-500 mt-2">Ocupación base en tiempo real</p>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {data && tipoReporte === "operadores" && (
           <div className="space-y-6 m-4">
               <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                   <div className="p-6 border-b border-gray-100">
                       <h3 className="text-base font-semibold text-gray-900">Desempeño</h3>
                   </div>
                   <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left">Operador</th>
                                <th className="px-6 py-3 text-left">Vehículos</th>
                                <th className="px-6 py-3 text-left">Ingresos</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.operadores.map((op, idx) => (
                                <tr key={idx}>
                                    <td className="px-6 py-4 font-medium">{op.nombre}</td>
                                    <td className="px-6 py-4">{op.vehiculos}</td>
                                    <td className="px-6 py-4 font-semibold text-teal-600">{formatCurrency(op.ingresos)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                   </div>
               </div>
           </div>
        )}
        
        {data && tipoReporte === "auditoria" && (
            <div className="space-y-6 m-4">
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left">Fecha</th>
                                <th className="px-4 py-3 text-left">Usuario</th>
                                <th className="px-4 py-3 text-left">Acción</th>
                                <th className="px-4 py-3 text-left">Módulo</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {data.auditoria.map((log, idx) => (
                                <tr key={idx}>
                                    <td className="px-4 py-3 text-gray-500">{new Date(log.fecha).toLocaleString()}</td>
                                    <td className="px-4 py-3 font-medium">{log.usuario}</td>
                                    <td className="px-4 py-3">{log.accion}</td>
                                    <td className="px-4 py-3"><span className="bg-slate-100 px-2 py-1 rounded">{log.modulo}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        </div> 
        {/* FIN CONTENEDOR REPORTE */}

      </div>
  );
}
